import React, { Component } from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  IconButton,
  Button,
  AppBar,
  Toolbar,
  Divider,
  Snackbar,
  Tooltip
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Sync as SyncIcon,
  VideocamOff as VideocamOffIcon,
  SelfImprovement as SelfImprovementIcon,
  Paid as PaidIcon,
  Face as FaceIcon,
} from "@mui/icons-material";
import tuljapurLogo from "../../assets/tuljapur-trust.png"; // Assuming you have a logo for Tuljapur Temple
import globalVariables from "../../custom_libraries/globalVariables";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";

// ---------------------------------------------------------------------------
// Devotee Flow charts (data comes from "Overall_People_Data_count" on the socket)
// ---------------------------------------------------------------------------
const CHART_FONT = '"Roboto", "Helvetica", "Arial", sans-serif';
const HOUR_CATEGORIES = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

// ---------------------------------------------------------------------------
// Header banner + hall card visuals (presentation only)
// ---------------------------------------------------------------------------
const MARATHI_FONT = '"Noto Serif Devanagari", "Mukta", "Mangal", "Nirmala UI", serif';

// Optional real banner photo, e.g.
//   import templeBanner from "../../assets/tuljapur-header-bg.jpg";
//   const HEADER_BG_IMAGE = templeBanner;
// When empty, a generated sunset + temple-silhouette banner is used instead.
const HEADER_BG_IMAGE = "";

const buildTempleSilhouette = () => {
  const tower = (cx, base, w, tiers, h) => {
    let d = "";
    for (let i = 0; i < tiers; i += 1) {
      const tw = w * (1 - (i / tiers) * 0.82);
      d += `<rect x="${cx - tw / 2}" y="${base - (i + 1) * h}" width="${tw}" height="${h}"/>`;
    }
    const top = base - tiers * h;
    return `${d}<rect x="${cx - 1.5}" y="${top - 10}" width="3" height="10"/><circle cx="${cx}" cy="${top - 12}" r="3"/>`;
  };
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 620 90" preserveAspectRatio="xMaxYMax meet">` +
    `<defs><linearGradient id="s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#5a2f5e" stop-opacity="0"/>` +
    `<stop offset="0.6" stop-color="#d9622b" stop-opacity="0.55"/><stop offset="1" stop-color="#f6a23c" stop-opacity="0.85"/></linearGradient></defs>` +
    `<rect width="620" height="90" fill="url(#s)"/>` +
    `<g fill="#1a1020" fill-opacity="0.92">` +
    `<rect x="0" y="80" width="620" height="10"/>` +
    tower(330, 80, 54, 9, 5) + tower(420, 80, 64, 11, 5) + tower(515, 80, 58, 10, 5) + tower(590, 80, 40, 7, 5) +
    `</g></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

const HEADER_BG_LAYERS = [
  // 1) dark fade from the left so the title stays readable
  "linear-gradient(90deg, rgba(8,15,32,0.98) 0%, rgba(8,15,32,0.88) 38%, rgba(8,15,32,0.25) 72%, rgba(8,15,32,0.6) 100%)",
  // 2) banner photo or generated temple silhouette
  HEADER_BG_IMAGE ? `url("${HEADER_BG_IMAGE}")` : buildTempleSilhouette(),
  // 3) base colour wash
  "linear-gradient(180deg, #0b1426 0%, #111c36 100%)",
].join(", ");

// Temporary queue-camera placeholder for the hall cards (barricade rails + devotees)
const buildHallPlaceholder = () => {
  const shirts = ["#e53935", "#fdd835", "#1e88e5", "#fb8c00", "#8e24aa", "#43a047"];
  let people = "";
  for (let r = 0; r < 3; r += 1) {
    for (let i = 0; i < 15; i += 1) {
      const x = 10 + i * 22 + (r % 2) * 10;
      const y = 52 + r * 14;
      people +=
        `<circle cx="${x}" cy="${y}" r="${4 + r}" fill="#c9a27b"/>` +
        `<rect x="${x - 4 - r}" y="${y + 4 + r}" width="${8 + 2 * r}" height="${10 + 2 * r}" rx="3" fill="${shirts[(i + r * 2) % 6]}"/>`;
    }
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 110" preserveAspectRatio="xMidYMid slice">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#3a455f"/><stop offset="1" stop-color="#141b2e"/></linearGradient></defs>` +
    `<rect width="320" height="110" fill="url(#g)"/>` +
    `<rect y="0" width="320" height="26" fill="#586178" fill-opacity="0.55"/>` +
    people +
    `<g stroke="#cfd5df" stroke-opacity="0.8" stroke-width="1.5"><line x1="0" y1="76" x2="320" y2="76"/><line x1="0" y1="96" x2="320" y2="96"/></g>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const HALL_IMAGE_PLACEHOLDER = buildHallPlaceholder();

// Dark Theme Configuration
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#191e29",
      paper: "#212632",
    },
    text: {
      primary: "#ffffff",
      secondary: "#bbbbbb",
    },
    primary: {
      main: "#61dafb",
    },
    warning: {
      main: "#ff9800",
    },
    error: {
      main: "#f44336",
    },
    success: {
      main: "#4caf50",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 8,
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
  },
});

// Class-based Error Boundary Component (D12: One widget failure must not crash dashboard)
class WidgetErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Widget Error caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card sx={{ p: 2, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Alert severity="error" icon={<ErrorIcon />}>
            Widget Temporarily Unavailable
          </Alert>
        </Card>
      );
    }
    return this.props.children;
  }
}

class CrowdMonitoring extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lastRefreshed: new Date().toLocaleTimeString(),
      isRefreshing: false,
      // "connecting" | "connected" | "disconnected" | "error"
      connectionStatus: "connecting",
      // Operational Data State
      dashboardData: {
        Overall_Data: [
          {
            People_Waiting: 0,
            Current_Throughput: 0,
            Overall_Darshan_Time: "-",
            Flow_Status: null,
          },
        ],
       // Populated live from "headcount.snapshot" websocket events, keyed by camera_id
        halls: [],
        alerts: [],
        last_updated: null,
        // Darshan ETAs from the socket: { Normal, Paid, Mukh } e.g. "144 to 150 min"
        darshanEta: { Normal: "-", Paid: "-", Mukh: "-" },
      },
      // Devotee flow charts. hasData stays false until the first socket payload
      // containing Overall_People_Data_count arrives (charts then show "Waiting for data...").
      devoteeFlow: {
        hourly: new Array(24).fill(0),
        daily: [],
        todayTotal: null,
        hasData: false,
      },
      snackbar_msg: '',
      open_snackbar: false,
      isSocketDataAvailable: false,
    };
  }

  socket = null;
  reconnectTimeout = null;
  reconnectAttempts = 0;
  manualClose = false;

  componentDidMount() {
    this.connectWebSocket();
  }

  componentWillUnmount() {
    this.manualClose = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      this.socket.close();
    }
  }

  // D-series: crowd count arrives over a websocket as it changes, no polling
  connectWebSocket = () => {
     let authToken = localStorage.getItem(globalVariables.AUTH_TOKEN);
    const wsUrl = `wss://devapi.neudeep.com/ws/crowd-monitoring/?token=${authToken}`;

    try {
      this.socket = new WebSocket(wsUrl);
    } catch (err) {
      console.error("Failed to open WebSocket:", err);
      this.setState({ connectionStatus: "error" });
      this.scheduleReconnect();
      return;
    }

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.setState({ connectionStatus: "connected" });
    };

    this.socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
        console.log("socket response---", data);
      } catch (err) {
        console.error("Malformed websocket payload:", event.data, err);
        return;
      }
      this.handleSocketEvent(data);
    };

    this.socket.onerror = (err) => {
      console.error("WebSocket error:", err);
      this.setState({ connectionStatus: "error" });
    };

    this.socket.onclose = () => {
      if (this.manualClose) return;
      this.setState({ connectionStatus: "disconnected" });
      this.scheduleReconnect();
    };
  };

  // Central dispatcher: route each socket event to its section by event_type.
  // Add a new case here whenever another event_type needs to feed a different
  // part of the dashboard (throughput, alerts, flow status, etc).
  handleSocketEvent = (data) => {
    if (!data || !data.section){
      // this.openSnackbar("No data is available yet, wait for upto 5 minutes", "error");
      this.setState({ isSocketDataAvailable: false });
      return;
    }

    switch (data.section) {
      case "Darshan ETA":
        this.handleHeadcountSnapshot(data);
        break;
      default:
        console.warn("Unhandled websocket section:", data.section, data);
    }
  };

  // event_type: "headcount.snapshot" -> one row per camera in the Hall
  // Occupancy table. Only camera_name (-> hall name) and payload.count
  // (-> current) are known from this event; everything else we don't have
  // yet (capacity/percent/eta) is shown as "-".
  handleHeadcountSnapshot = (data) => {
    this.setState({ isSocketDataAvailable: true });
    this.handleDevoteeFlowData(data);
    const hallsData = data?.halls;

    if (!hallsData || typeof hallsData !== "object") {
      return;
    }

    const halls = Object.entries(hallsData).map(([cameraKey, hall]) => ({
      // Keep camera name as unique identifier
      cameraId: cameraKey,

      // Display name
      id: hall.camera_name || cameraKey,

      // Current people count
      current:
        typeof hall.people_count === "number"
          ? hall.people_count
          : 0,

      // Hall capacity
      capacity:
        typeof hall.capacity === "number"
          ? hall.capacity
          : "-",

      // Occupancy percentage
      percent:
        typeof hall.occupancy_percent === "number"
          ? hall.occupancy_percent
          : "-",

      // min & max ETA
      eta:
        typeof hall.eta_min_minutes === "number" &&
        typeof hall.eta_max_minutes === "number"
          ? `${this.formatEta(hall.eta_min_minutes)} to ${this.formatEta(hall.eta_max_minutes)}`
          : "-",

      crowd: hall.crowd_state || "-",

      // Status from API
      movement: hall.movement || "UNKNOWN",

      // Latest camera snapshot (signed S3 URL)
      imageUrl: hall.image_url || null,

    }));

    const alerts = this.generateAlertsAndExceptions(data);

    const Overall_Data = data?.Overall_Data || [];
    const Overall_People_Data_count = data?.Overall_People_Data_count || {};
    const eta = this.extractDarshanEta(data);

    this.setState({
      lastRefreshed: new Date().toLocaleTimeString(),

      dashboardData: {
        ...this.state.dashboardData,
        halls,
        Overall_Data,
        Overall_People_Data_count,
        alerts,
        last_updated: data?.published_at || null,
        // keep the previous values if this payload has no darshan ETA object
        darshanEta: eta
          ? { Normal: eta.Normal ?? "-", Paid: eta.Paid ?? "-", Mukh: eta.Mukh ?? "-" }
          : this.state.dashboardData.darshanEta,
      },
    });
  };

  // Maps data.Overall_People_Data_count -> devotee flow charts.
  // Shape: { <group_key>: { today_total, hourly_devotee_flow_today: [{hour:"14:00", total_count}],
  //                         daily_devotee_flow_last_7_days: [{date:"2026-10-11", total_count}] } }
  // If several groups are present their counts are summed. The 30-day list is ignored.
  handleDevoteeFlowData = (data) => {
    const groups = data?.Overall_People_Data_count;
    if (!groups || typeof groups !== "object") return;

    const hourly = new Array(24).fill(0);
    const dailyMap = new Map(); // "YYYY-MM-DD" -> count
    let todayTotal = 0;
    let found = false;

    Object.values(groups).forEach((g) => {
      if (!g || typeof g !== "object") return;
      found = true;
      todayTotal += Number(g.today_total) || 0;

      (g.hourly_devotee_flow_today || []).forEach((h) => {
        const hr = parseInt(h?.hour, 10); // "14:00" -> 14
        if (Number.isInteger(hr) && hr >= 0 && hr < 24) {
          hourly[hr] += Number(h.total_count) || 0;
        }
      });

      (g.daily_devotee_flow_last_7_days || []).forEach((d) => {
        if (!d?.date) return;
        dailyMap.set(d.date, (dailyMap.get(d.date) || 0) + (Number(d.total_count) || 0));
      });
    });

    if (!found) return;

    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const daily = Array.from(dailyMap.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .slice(-7)
      .map(([date, count]) => {
        const [, m, d] = date.split("-");
        const label = MONTHS[Number(m) - 1] ? `${Number(d)} ${MONTHS[Number(m) - 1]}` : date;
        return { date: label, count };
      });

    this.setState({
      devoteeFlow: { hourly, daily, todayTotal, hasData: true },
    });
  };

  // Options shared by both bar charts so they look consistent with the dark theme
  getBaseChartOptions = () => ({
    chart: {
      type: "bar",
      background: "transparent",
      fontFamily: CHART_FONT,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, speed: 500 },
    },
    theme: { mode: "dark" },
    dataLabels: { enabled: false },
    legend: { show: false },
    // Shown by ApexCharts when the series is empty (no socket data yet)
    noData: {
      text: "Waiting for data...",
      align: "center",
      verticalAlign: "middle",
      style: { color: "#9e9e9e", fontSize: "14px", fontFamily: CHART_FONT },
    },
    grid: {
      borderColor: "rgba(255, 255, 255, 0.08)",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    plotOptions: {
      bar: { borderRadius: 4, borderRadiusApplication: "end" },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        style: { colors: "#bbbbbb", fontSize: "12px", fontFamily: CHART_FONT },
        // whole numbers only (avoids repeated labels when counts are tiny)
        formatter: (v) => (Number.isInteger(v) ? v.toLocaleString("en-IN") : ""),
      },
    },
    states: {
      hover: { filter: { type: "lighten", value: 0.12 } },
    },
    tooltip: {
      theme: "dark",
      style: { fontSize: "13px", fontFamily: CHART_FONT },
    },
  });

  getHourlyChartOptions = (hourly) => {
    const base = this.getBaseChartOptions();
    const peak = Math.max(...hourly);
    const peakIndex = peak > 0 ? hourly.indexOf(peak) : -1;
    const hourRange = (i) =>
      `${String(i).padStart(2, "0")}:00 - ${String((i + 1) % 24).padStart(2, "0")}:00`;

    return {
      ...base,
      // Peak hour highlighted in amber, rest in primary cyan
      colors: [
        ({ dataPointIndex }) => (dataPointIndex === peakIndex ? "#ffb74d" : "#61dafb"),
      ],
      plotOptions: {
        bar: { ...base.plotOptions.bar, columnWidth: "70%", dataLabels: { position: "top" } },
      },
      // Count label only on the peak (amber) bar; every other bar stays unlabeled.
      // Top padding keeps the label inside the chart area.
      dataLabels: {
        enabled: peakIndex >= 0,
        offsetY: -20,
        formatter: (val, opts) =>
          opts.dataPointIndex === peakIndex && typeof val === "number" ? val.toLocaleString("en-IN") : "",
        style: { fontSize: "12px", fontWeight: 600, fontFamily: CHART_FONT, colors: ["#ffb74d"] },
        background: { enabled: false },
        dropShadow: { enabled: false },
      },
      grid: { ...base.grid, padding: { top: 22 } },
      yaxis: { ...base.yaxis, max: peak === 0 ? 5 : undefined },
      // transparent stroke leaves a visible gap so every hour reads as its own bar
      stroke: { show: true, width: 3, colors: ["transparent"] },
      xaxis: {
        type: "category",
        categories: HOUR_CATEGORIES,
        tickAmount: 24,
        tickPlacement: "on",
        axisBorder: { color: "rgba(255, 255, 255, 0.15)" },
        axisTicks: { show: false },
        title: {
          text: "Hour of day (24h)",
          style: { color: "#bbbbbb", fontSize: "12px", fontWeight: 400, fontFamily: CHART_FONT },
        },
        labels: {
          rotate: 0,
          hideOverlappingLabels: false,
          trim: false,
          style: { colors: "#bbbbbb", fontSize: "12px", fontFamily: CHART_FONT },
          // Show even hours only; all 24 bars remain
          formatter: (val) => (parseInt(val, 10) % 2 === 0 ? String(val).slice(0, 2) : ""),
        },
        tooltip: { enabled: false },
      },
      tooltip: {
        ...base.tooltip,
        x: { formatter: (_v, opts) => hourRange(opts.dataPointIndex) },
        y: { title: { formatter: () => "Devotees:" }, formatter: (v) => v.toLocaleString("en-IN") },
      },
    };
  };

  getDailyChartOptions = (daily) => {
    const base = this.getBaseChartOptions();
    const lastIndex = daily.length - 1;
    const dailyPeak = Math.max(0, ...daily.map((d) => d.count));

    return {
      ...base,
      // Today highlighted in cyan, previous days in indigo
      colors: [
        ({ dataPointIndex }) => (dataPointIndex === lastIndex ? "#61dafb" : "#7986cb"),
      ],
      yaxis: { ...base.yaxis, max: dailyPeak === 0 ? 5 : undefined },
      plotOptions: {
        bar: { ...base.plotOptions.bar, columnWidth: "45%", dataLabels: { position: "top" } },
      },
      // Value shown above every bar (no hover needed); top padding keeps the tallest label inside the chart
      dataLabels: {
        enabled: true,
        offsetY: -20,
        formatter: (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v),
        style: { fontSize: "12px", fontWeight: 600, fontFamily: CHART_FONT, colors: ["#e0e0e0"] },
        background: { enabled: false },
        dropShadow: { enabled: false },
      },
      grid: { ...base.grid, padding: { top: 22 } },
      xaxis: {
        categories: daily.map((d) => d.date),
        axisBorder: { color: "rgba(255, 255, 255, 0.15)" },
        axisTicks: { show: false },
        labels: {
          rotate: 0,
          style: { colors: "#bbbbbb", fontSize: "12px", fontFamily: CHART_FONT },
        },
        tooltip: { enabled: false },
      },
      tooltip: {
        ...base.tooltip,
        y: { title: { formatter: () => "Devotees:" }, formatter: (v) => v.toLocaleString("en-IN") },
      },
    };
  };

  renderDevoteeFlowCharts = () => {
    const { hourly, daily, todayTotal, hasData } = this.state.devoteeFlow;
    const hourlyTotal = typeof todayTotal === "number" ? todayTotal : hourly.reduce((a, b) => a + b, 0);

    const header = (title, subtitle) => (
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
        </Box>
        
      </Box>
    );

    // Card fills its slot; chart area takes the remaining height (absolute inner box
    // gives ApexCharts a definite height to resolve height="100%" against).
    const chartCard = (title, subtitle, options, data) => (
      <WidgetErrorBoundary>
        <Card sx={{ height: "100%" }}>
          <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", py: 1.5, "&:last-child": { pb: 1 } }}>
            {header(title, subtitle)}
            <Box sx={{ flex: 1, minHeight: 140, position: "relative", mt: 0.5 }}>
              <Box sx={{ position: "absolute", inset: 0 }}>
                <Chart type="bar" height="100%" width="100%" options={options} series={[{ name: "Devotees", data }]} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </WidgetErrorBoundary>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, height: "100%" }}>
        <Box 
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            "& .MuiTypography-root": {
              fontSize: { xs: "0.95rem", md: "1.1rem" },
            },
          }}
        >
          {chartCard("Hourly Devotee Flow", hasData ? "Today" : "Today", this.getHourlyChartOptions(hourly), hasData ? hourly : [])}
        </Box>
        <Box 
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            "& .MuiTypography-root": {
              fontSize: { xs: "0.95rem", md: "1.1rem" },
            },
          }}
        >
          {chartCard("Daily Devotee Flow", "Last 7 days", this.getDailyChartOptions(daily), hasData ? daily.map((d) => d.count) : [])}
        </Box>
      </Box>
    );
  };

  formatLastUpdated = (timestamp) => {
      if (!timestamp) {
        return "-";
      }

      const date = new Date(timestamp);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }).format(date);
  };

  formatEta = (minutes) => {
  if (typeof minutes !== "number" || minutes < 0) {
    return "-";
  }

  const totalMinutes = Math.floor(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours === 0) {
    return `${remainingMinutes} min`;
  }

  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }

  return `${hours} hr ${remainingMinutes} min`;
};

  // Finds the { Normal, Paid, Mukh } darshan-ETA object in a socket payload: top level first,
  // then Overall_Data, then any other nested object (so the wrapper key name doesn't matter).
  extractDarshanEta = (data) => {
    const isEta = (o) =>
      !!o && typeof o === "object" && ["Normal", "Paid", "Mukh"].some((k) => k in o);
    if (isEta(data)) return data;
    if (isEta(data?.Overall_Data)) return data.Overall_Data;
    return Object.values(data || {}).find(isEta) || null;
  };

  // "144 to 150 min" (or a number / "30 min") -> "2 hr 24 min – 2 hr 30 min" via formatEta().
  // Anything that can't be parsed (e.g. "Not Available") is shown as received.
  formatEtaRange = (value) => {
    if (typeof value === "number") return this.formatEta(value);
    if (typeof value !== "string" || !value.trim() || value.trim() === "-") return "-";
    if (/hr|hour/i.test(value)) return value; // already formatted
    const nums = value.match(/\d+(?:\.\d+)?/g);
    if (!nums) return value;
    const [a, b] = nums.map(Number);
    if (nums.length === 1 || a === b) return this.formatEta(a);
    return `${this.formatEta(a)} – ${this.formatEta(b)}`;
  };

  scheduleReconnect = () => {
    if (this.manualClose) return;
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    // Exponential backoff, capped at 30s
    const delay = Math.min(30000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimeout = setTimeout(() => {
      this.connectWebSocket();
    }, delay);
  };

  handleRefresh = () => {
    this.setState({ isRefreshing: true });
    // Simulate dashboard re-fetch / refresh
    setTimeout(() => {
      this.setState({
        lastRefreshed: new Date().toLocaleTimeString(),
        isRefreshing: false,
      });
    }, 600);
  };

  getCapacityColor = (percent, status) => {
    if (status === "DEGRADED") return "warning";
    if (typeof percent !== "number") return "info";
    if (percent >= 90) return "error";
    if (percent >= 80) return "warning";
    if (percent < 80) return "success";
    return "success";
  };

  generateAlertsAndExceptions = (data) => {
    const alerts = [];

    const processItems = (items) => {
      if (!items || typeof items !== "object") {
        return;
      }

      Object.entries(items).forEach(([key, item]) => {
        if (!item || typeof item !== "object") {
          return;
        }

        const cameraName = item.camera_name || key;
        const status = item.status;
        const movement = item.movement;
        const occupancy_percent = item.occupancy_percent;

        // Camera/Hall status exception
        if (status === "UNKNOWN" || status === "DEGRADED") {
          alerts.push({
            id: `${cameraName}-status`,
            severity: status === "DEGRADED" ? "error" : "warning",
            message: `${cameraName} status is ${status}`,
          });
        }

        // Camera/Hall movement exception
        if (movement === "STOPPED" || movement === "UNKNOWN") {
          alerts.push({
            id: `${cameraName}-movement`,
            severity: movement === "STOPPED" ? "error" : "warning",
            message: `${cameraName} movement is ${movement}`,
          });
        }

        //occupancy_percent
        if (typeof occupancy_percent === "number" && occupancy_percent >= 90) {
          alerts.push({
            id: `${cameraName}-occupancy`,
            severity: this.getCapacityColor(occupancy_percent, status),
            message: `${cameraName} occupancy is ${occupancy_percent.toFixed(1)}%`,
          });
        }
      });
    };

    // Process halls
    processItems(data?.halls);

    // Process cameras
    processItems(data?.cameras);

    return alerts;
  };

  openSnackbar = (msg, alert_type = 'warning' || 'success' || 'error' || 'info') => { //function to show the alerts
    console.log("Opening snackbar", new Date().toISOString());
    this.setState({
      open_snackbar: true,
      snackbar_msg: msg,
      alert_type: alert_type,
    })
  }

  handleSnackbarClose = () => {
    this.setState({
      open_snackbar: false,
    });
  };

  renderOverviewCards = () => {
    const { Overall_Data, darshanEta, Overall_People_Data_count } = this.state.dashboardData;
    const { isSocketDataAvailable } = this.state;

    const darshanCards = [
      { key: "Normal", label: "Dharma Darshan", icon: <SelfImprovementIcon color="primary" /> },
      { key: "Paid", label: "Paid Darshan", icon: <PaidIcon color="success" /> },
      { key: "Mukh", label: "Mukha Darshan", icon: <FaceIcon color="secondary" /> },
    ];

    return (
      <>
      {isSocketDataAvailable === false && (
        <Alert severity="warning" sx={{ width: "100%", mb: 1 }}>
          "No data is available yet, please check if camera(s) are armed and wait for upto 5 minutes."
        </Alert>
      )}
        <Grid container spacing={1.5}>
          
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent
              sx={{
                px: 1.5,
                py: 0.75,
                "&:last-child": {
                  pb: 0.75,
                },
              }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" justifyContent="space-between">
                  <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: "0.875rem", md: "0.78rem" }, lineHeight: 1.25 }}>
                    Overall People Waiting
                  </Typography>
                  <Tooltip title="This metric represents the total number of people currently waiting across all halls and ramps/pathways and staircase."
                                      placement="right"
                                      arrow
                                      componentsProps={{
                                      tooltip: {
                                        sx: {
                                          backgroundColor: '#283348',
                                          color: '#fff',
                                          fontSize: '13px',
                                          border: '1px solid #99acb8',
                                        },
                                      },
                                      arrow: {
                                        sx: {
                                          color: '#283348',
                                        },
                                      },
                                  }}
                                    >
                                    <InfoOutlinedIcon
                                      style={{
                                        fontSize: "22px",
                                        marginLeft: "6px",
                                        verticalAlign: "middle",
                                        color: "#bac6d6",
                                        cursor: "pointer"
                                      }}
                                    />
                                </Tooltip>
                  </Box>
                  <PeopleIcon color="primary" />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0, lineHeight: 1.3 }}>
                  {Overall_Data.People_Waiting?.toLocaleString() || '-'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent
                sx={{
                  px: 1.5,
                py: 0.75,
                  "&:last-child": {
                    pb: 0.75,
                  },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: "0.875rem", md: "0.78rem" }, lineHeight: 1.25 }}>
                    Overall Current Throughput
                  </Typography>
                  <SpeedIcon color="primary" />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0, lineHeight: 1.3 }}>
                  {Overall_Data.Current_Throughput?.toLocaleString() || '-'}
                  {/* <Typography component="span" variant="body2" color="textSecondary">persons/min</Typography> */}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {darshanCards.map(({ key, label, icon }) => (
            <Grid item xs={12} sm={6} md={2} key={key}>
              <Card sx={{ height: "100%" }}>
                <CardContent
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    "&:last-child": {
                      pb: 0.75,
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                    <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: "0.875rem", md: "0.78rem" }, lineHeight: 1.25 }}>
                      {label}
                    </Typography>
                    {icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 0, fontSize: { xs: "1.1rem", md: "0.95rem", xl: "1.05rem" }, lineHeight: 1.3 }}>
                    {this.formatEtaRange(darshanEta?.[key])}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent
                sx={{
                  px: 1.5,
                py: 0.75,
                  "&:last-child": {
                    pb: 0.75,
                  },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: "0.875rem", md: "0.78rem" }, lineHeight: 1.25 }}>
                    Total Devotees Today
                  </Typography>
                  <CheckCircleIcon color="success" />
                </Box>
                {/* {Overall_Data.Flow_Status && (
                <Box sx={{ mt: 0.25 }}>
                  <Chip
                    label={Overall_Data.Flow_Status}
                    color={(Overall_Data.Flow_Status === "MOVING" || Overall_Data.Flow_Status === "MOVING_FAST") ? "success" : (Overall_Data.Flow_Status === "STOPPED" ? "error" : "warning")}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                </Box>
                )} */}
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0, lineHeight: 1.3 }}>
                  {Overall_People_Data_count?.camera_10_14_15?.today_total || "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
  };

  renderHallOccupancyTable = () => {
    const { halls } = this.state.dashboardData;

    // Visual tone per hall, driven by occupancy % (and STOPPED flow)
    const TONES = {
      error: {
        header: "linear-gradient(90deg, #c62828 0%, #e53935 100%)",
        headerText: "#ffffff",
        bar: "#e53935",
        boxBg: "#fde8e8",
        boxText: "#c62828",
        icon: <ErrorIcon sx={{ fontSize: 22, color: "#d32f2f" }} />,
      },
      warning: {
        header: "linear-gradient(90deg, #f0b429 0%, #f7cb4d 100%)",
        headerText: "#2e2200",
        bar: "#f0a800",
        boxBg: "#fdf3cf",
        boxText: "#8a5a00",
        icon: <WarningIcon sx={{ fontSize: 22, color: "#f0a800" }} />,
      },
      success: {
        header: "linear-gradient(90deg, #2e9e57 0%, #43b86c 100%)",
        headerText: "#ffffff",
        bar: "#2e9e57",
        boxBg: "#dff3e4",
        boxText: "#1f7a40",
        icon: <CheckCircleIcon sx={{ fontSize: 22, color: "#2e9e57" }} />,
      },
      neutral: {
        header: "linear-gradient(90deg, #546174 0%, #6b7a8f 100%)",
        headerText: "#ffffff",
        bar: "#90a4ae",
        boxBg: "#eceff3",
        boxText: "#546174",
        icon: <VideocamOffIcon sx={{ fontSize: 22, color: "#78909c" }} />,
      },
    };

    const getHallTone = (hall) => {
      if (typeof hall.percent !== "number") {
        return { ...TONES.neutral, hint: "Awaiting occupancy data" };
      }
      if (hall.percent >= 90) {
        return { ...TONES.error, hint: "Needs immediate attention" };
      }
      if (hall.movement === "STOPPED") {
        return { ...TONES.error, hint: "Queue movement stopped" };
      }
      if (hall.percent >= 50) {
        return { ...TONES.warning, hint: "Monitor closely" };
      }
      return { ...TONES.success, hint: "Space available" };
    };

    // Movement status pill shown beside the camera name (same movement values as before)
    const getMovementPill = (movement) => {
      const map = {
        UNKNOWN: { label: "UNKNOWN", color: "#b26a00" },
        STOPPED: { label: "STOPPED", color: "#c62828" },
        SLOW: { label: "SLOW", color: "#b26a00" },
        MOVING: { label: "MOVING", color: "#1f7a40" },
        MOVING_FAST: { label: "MOVING", color: "#1f7a40" },
        NORMAL: { label: "NORMAL", color: "#1e6fd9" },
      };
      return map[movement] || null;
    };

    return (
      <Card 
        sx={{ 
          flex: 1, 
          minWidth: 0, 
          minHeight: 0, 
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "7px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#094a8b",
            borderRadius: "8px",
          },
       }}>
        <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
          {/* <Typography variant="h6" fontWeight="bold" fontSize={{ xs: "0.95rem", md: "1.1rem" }}>
            Hall Occupancy & ETA Breakdown
          </Typography> */}

          {halls.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              Waiting for camera data...
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                gap: 1,
                mt: 0,
              }}
            >
              {halls.map((hall) => {
                const tone = getHallTone(hall);
                const pill = getMovementPill(hall.movement);
                const hasCapacity = typeof hall.capacity === "number";
                const hasPercent = typeof hall.percent === "number";
                const hasEta = hall.eta && hall.eta !== "-";
                const showCameraId = hall.cameraId && hall.cameraId !== hall.id;

                return (
                  <Box
                    key={hall.cameraId ?? hall.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "#ffffff",
                      color: "#1b2a41",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
                    }}
                  >
                    {/* Coloured header: hall name + movement status */}
                    <Box
                      sx={{
                        background: tone.header,
                        color: tone.headerText,
                        px: 1.25,
                        py: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                        <PeopleIcon sx={{ fontSize: 18 }} />
                        <Typography noWrap fontWeight="bold" sx={{ fontSize: "0.88rem", lineHeight: 1.3, color: "inherit" }}>
                          {hall.id}
                        </Typography>
                        {showCameraId && (
                          <Typography noWrap sx={{ fontSize: "0.7rem", color: "inherit", opacity: 0.85 }}>
                            ({hall.cameraId})
                          </Typography>
                        )}
                      </Box>
                      {pill && (
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.1,
                            borderRadius: 10,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            bgcolor: "#ffffff",
                            color: pill.color,
                          }}
                        >
                          <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: pill.color }} />
                          {pill.label}
                        </Box>
                      )}
                    </Box>

                    {/* Camera snapshot (image_url from the socket; placeholder if missing / fails to load) */}
                    <Box sx={{ height: { xs: 130, md: 115 }, backgroundColor: "#10182b", lineHeight: 0 }}>
                      <Box
                        component="img"
                        src={this.getHallImage(hall)}
                        alt={`${hall.id} camera snapshot`}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = HALL_IMAGE_PLACEHOLDER;
                        }}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </Box>

                    {/* Occupancy */}
                    <Box sx={{ px: 1.25, pt: 0.75 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                          <PeopleIcon sx={{ fontSize: 20, color: "#1e6fd9" }} />
                          <Box minWidth={0}>
                            <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.2, color: "#1b2a41" }}>
                              {hasCapacity ? `${hall.current} / ${hall.capacity}` : hall.current}
                            </Typography>
                            <Typography sx={{ fontSize: "0.65rem", lineHeight: 1.2, color: "#5b6b82" }}>
                              Current Occupancy
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1.2, color: "#1b2a41" }}>
                          {hasPercent ? `${hall.percent}%` : "-"}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={hasPercent ? Math.min(100, Math.max(0, hall.percent)) : 0}
                        sx={{
                          mt: 0.5,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: "#e3e7ee",
                          "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: tone.bar },
                        }}
                      />
                    </Box>

                    {/* ETA */}
                    <Box sx={{ px: 1.25, pt: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}>
                      <AccessTimeIcon sx={{ fontSize: 20, color: hasEta ? "#1e6fd9" : "#9aa7b8" }} />
                      <Box minWidth={0}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.2, color: "#1b2a41" }}>
                          {hasEta ? hall.eta.replace(" to ", " – ") : "Not Available"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.65rem", lineHeight: 1.2, color: "#5b6b82" }}>
                          Estimated Waiting Time
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status box: crowd state from API + guidance */}
                    <Box
                      sx={{
                        m: 0.75,
                        mt: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1.5,
                        bgcolor: tone.boxBg,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {tone.icon}
                      <Box minWidth={0}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1.2, color: tone.boxText }}>
                          {hall.crowd}
                        </Typography>
                        <Typography sx={{ fontSize: "0.65rem", lineHeight: 1.2, color: tone.boxText, opacity: 0.9 }}>
                          {tone.hint}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Camera snapshot for a hall card: live image_url from the socket, else the placeholder.
  getHallImage = (hall) => hall?.imageUrl || HALL_IMAGE_PLACEHOLDER;

  renderAlertsAndExceptions = () => {
  const { alerts } = this.state.dashboardData;

  return (
    <Card 
    sx={{ flex: 1,
      minWidth: 0, 
      minHeight: 0, 
      overflowY: "auto",
      "&::-webkit-scrollbar": { width: "5px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#1976d2",
        borderRadius: "8px",
      },
      }}
    >
      <CardContent>
        <Typography variant="h6" fontWeight="bold" fontSize={{ xs: "0.95rem", md: "1.1rem" }} gutterBottom>
          Alerts & Exceptions
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{ mt: 2 }}
        >
          {alerts.length > 0 ? (
            alerts.map((alert) => (
              <Alert
                key={alert.id}
                severity={alert.severity}
                variant="filled"
              >
                {alert.message}
              </Alert>
            ))
          ) : (
            <Alert severity="success" variant="outlined">
              No alerts or exceptions
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

  // renderPublicDisplayWidget = () => {
  //   const { overallDarshanTime } = this.state.dashboardData;

  //   return (
  //     <Card sx={{ background: "linear-gradient(135deg, #1e2638 0%, #0d111a 100%)", border: "1px solid #3a4454" }}>
  //       <CardContent>
  //         <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={1.2}>
  //           Public-Facing Display Widget (C27)
  //         </Typography>
  //         <Grid container spacing={2} sx={{ mt: 1 }}>
  //           <Grid item xs={12} sm={6}>
  //             <Box p={2} sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: 1 }}>
  //               <Typography color="textSecondary" variant="body2">
  //                 Approximate Darshan Time
  //               </Typography>
  //               <Typography variant="h4" fontWeight="bold" color="primary.main">
  //                 {overallDarshanTime}
  //               </Typography>
  //             </Box>
  //           </Grid>
  //           <Grid item xs={12} sm={6}>
  //             <Box p={2} sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: 1 }}>
  //               <Typography color="textSecondary" variant="body2">
  //                 Current Crowd Level
  //               </Typography>
  //               <Typography variant="h4" fontWeight="bold" color="warning.main">
  //                 High
  //               </Typography>
  //             </Box>
  //           </Grid>
  //         </Grid>
  //       </CardContent>
  //     </Card>
  //   );
  // };

  renderConnectionStatusChip = () => {
    const { connectionStatus } = this.state;
    const map = {
      connected: { label: "Live", color: "success", icon: <CheckCircleIcon fontSize="small" /> },
      connecting: { label: "Connecting...", color: "warning", icon: <SyncIcon fontSize="small" sx={{ animation: "spin 1s infinite linear" }} /> },
      disconnected: { label: "Reconnecting...", color: "warning", icon: <WarningIcon fontSize="small" /> },
      error: { label: "Connection Error", color: "error", icon: <ErrorIcon fontSize="small" /> },
    };
    const { label, color, icon } = map[connectionStatus] || map.connecting;

    return <Chip size="small" label={label} color={color} icon={icon} variant="outlined" sx={{ mr: 2 }} />;
  };

  render() {
    const { lastRefreshed, isRefreshing, dashboardData } = this.state;

    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box sx={{ display: "flex", flexDirection: "column", height: { xs: "auto", md: "96vh" }, overflow: { xs: "visible", md: "auto" }, bgcolor: "background.default", pb: 2, px: { xs: 2, sm: 3, md: 3 } }}>
          {/* Top AppBar */}
 <AppBar
  position="static"
  color="default"
  elevation={0}
  sx={{
    borderBottom: "2px solid #c9a24a",
    ml: { xs: 1, sm: 2, md: 2 },
    // width: {
    //   xs: "calc(100% - 16px)",
    //   sm: "calc(100% - 32px)",
    //   md: "calc(100% - 48px)",
    // },
    width: "calc(100% - 32px)",
    backgroundColor: "#0b1426",
    // Layered banner: dark fade (keeps title readable) -> temple image / sunset + silhouette
    backgroundImage: HEADER_BG_LAYERS,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover, auto 100%, cover",
    backgroundPosition: "center, center, center",
  }}
>
  <Toolbar
    sx={{
      // Taller banner on desktop to fit the Marathi + English title; on mobile let the header grow and wrap
      minHeight: { xs: "auto !important", md: "56px !important" },
      height: { xs: "auto", md: "56px" },
      flexWrap: { xs: "wrap", md: "nowrap" },
      alignItems: "center",
      py: { xs: 0.5, md: 0 },
      px: { xs: 1.5, md: 2 },
    }}
  >
    <img
      src={tuljapurLogo}
      alt="Tuljapur Temple Logo"
      style={{
        width: 42,
        height: 54,
        marginRight: 14,
        objectFit: "contain",
        flexShrink: 0,
      }}
    />

    <Box
      sx={{
        flexGrow: 1,
        flexBasis: { xs: 0, md: "auto" },
        minWidth: 0,
      }}
    >
      {/* Marathi title */}
      <Typography
        variant="h6"
        // component="div"
        sx={{
          fontFamily: MARATHI_FONT,
          fontWeight: 700,
          fontSize: { xs: "1.15rem", md: "1.25rem" },
          lineHeight: 1.25,
          color: "#f6c25b",
          textShadow: "0 2px 6px rgba(0,0,0,0.6)",
        }}
      >
        श्री तुळजाभवानी मंदिर, तुळजापूर
      </Typography>

      {/* Existing English label */}
      {/* <Typography
        variant="h6"
        // component="div"
        sx={{
          fontWeight: 600,
          fontSize: { xs: "0.8rem", md: "1rem" },
          lineHeight: 1.3,
          color: "#ffffff",
          textShadow: "0 1px 4px rgba(0,0,0,0.6)",
        }}
      >
        Tuljapur Temple Trust - Crowd Monitoring Dashboard
      </Typography> */}
    </Box>

    {/* On mobile this group drops to its own full-width row under the title */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        width: { xs: "100%", md: "auto" },
        mt: { xs: 0.75, md: 0 },
      }}
    >
      {this.renderConnectionStatusChip()}

      <Typography
        variant="body2"
        sx={{
          ml: { xs: 0, md: 2 },
          pl: { xs: 0, md: 2 },
          fontSize: "0.8rem",
          color: "#cfd8e6",
          borderLeft: { xs: "none", md: "1px solid rgba(255,255,255,0.25)" },
        }}
      >
        Last updated: {this.formatLastUpdated(dashboardData?.last_updated)}
      </Typography>
    </Box>
  </Toolbar>
</AppBar>

          {/* Main Dashboard: three rows that share the viewport height (no page scroll on md+) */}
          <Box sx={{ p: 1.5, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.2 }}>
            {/* Row 1: Key Metrics (natural height) */}
            <Box sx={{ flexShrink: 0 }}>
              <WidgetErrorBoundary>
                {this.renderOverviewCards()}
              </WidgetErrorBoundary>
            </Box>

            {/* Row 2: Hall cards (8 cols, natural height - no inner scroll) & Alerts (4 cols, scrolls inside card if long) */}
            <Box sx={{ flex: { md: "0 0 auto" }, minHeight: { xs: 360, md: 0 }, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1.5 }}>
              <Box sx={{ flex: { md: "8 1 0" }, minWidth: 0, minHeight: 0, display: "flex" }}>
                <WidgetErrorBoundary>
                  {this.renderHallOccupancyTable()}
                </WidgetErrorBoundary>
              </Box>
              <Box sx={{ flex: { md: "4 1 0" }, minWidth: 0, minHeight: { xs: 200, md: 0 }, display: "flex", contain: { md: "size" } }}>
                <WidgetErrorBoundary>
                  {this.renderAlertsAndExceptions()}
                </WidgetErrorBoundary>
              </Box>
            </Box>

            {/* Row 3: Devotee Flow charts (hourly + 7-day) */}
            <Box 
              sx={{
                flex: { md: "0 0 32%" },
                height: { xs: "auto", md: "32%" },
                minHeight: { xs: 500, md: 220 },
              }}
            >
              {this.renderDevoteeFlowCharts()}
            </Box>

            {/* Footer */}
          <Box
            component="footer"
            sx={{
              width: "100%",
              py: 0.5,
              px: 2,
              textAlign: "center",
              backgroundColor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.78rem" },
                lineHeight: 1.8,
                color: "text.secondary",
              }}
            >
              Official information - <b>Shri Tulja Bhavani Temple Trust, Tuljapur.</b>
              {" | "}
              Smart Darshan Powered by <b>Neudeep Technologies Pvt. Ltd., Pune</b>
              {" | "}
              <Link
                href="https://www.neudeep.com"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: "inherit", fontWeight: 600 }}
              >
                www.neudeep.com
              </Link>
            </Typography>
          </Box>
          </Box>
        
        </Box>
      </ThemeProvider>
    );
  }
}

export default CrowdMonitoring;